import axios from "axios";
import { globalErrorHandler } from "../utils/ErrorHandler";
import { encryptMsalValue, decryptMsalValue } from "../utils/msalStorage";
import { csrfManager, setAutoLogoutReset } from "./AxiosClient";

// Base API URL
const BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_BASE_URLS;

// Re-export setAutoLogoutReset for convenience
export { setAutoLogoutReset };

// Global reset timer reference
let globalResetTimer = null;

/**
 * Set the auto-logout reset function
 * @param {Function} resetFn - Function to reset the auto-logout timer
 */
export const setAutoLogoutResetPayload = (resetFn) => {
  globalResetTimer = resetFn;
};

const safeJsonParse = (value) => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    console.error("Failed to parse MSAL storage entry:", error);
    return null;
  }
};

// Helper function to get auth token from sessionStorage
const getToken = () => {
  try {
    const keys = Object.keys(sessionStorage);
    const mslKeys = keys.filter(
      (k) => k.includes("msal") && k.includes("access_token"),
    );

    if (mslKeys.length > 0) {
      const tokenData = decryptMsalValue(sessionStorage.getItem(mslKeys[0]));
      if (tokenData) {
        const parsed = safeJsonParse(tokenData);
        return parsed?.secret || parsed?.access_token || null;
      }
    }

    const idTokenKey = decryptMsalValue(
      sessionStorage.getItem(
        `msal.token.keys.${process.env.REACT_APP_MSAL_CLIENT_ID}`,
      ),
    );

    if (!idTokenKey) {
      console.warn("⚠️ No MSAL token key found");
      return null;
    }

    const parsedIdTokenKey = safeJsonParse(idTokenKey);
    if (!parsedIdTokenKey?.idToken?.length) {
      console.warn("⚠️ Malformed MSAL token key payload");
      return null;
    }

    const token = decryptMsalValue(
      sessionStorage.getItem(`${parsedIdTokenKey.idToken[0]}`),
    );

    if (!token) {
      console.warn("⚠️ No token found in sessionStorage");
      return null;
    }

    const parsedToken = safeJsonParse(token);
    const tokenAuth = encryptMsalValue(parsedToken?.secret || null);
    return tokenAuth;
  } catch (error) {
    console.error("❌ Error retrieving auth token:", error);
    return null;
  }
};

// Create axios instance for payload-based authentication
const axiosClientPayload = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
});

// Handle unauthorized response
const handleUnauthorized = () => {
  console.warn("🚨 Unauthorized - Clearing session and redirecting");
  csrfManager.clearToken();
  globalErrorHandler.handleUnauthorized();
};

// Request interceptor - Add Auth Token to PAYLOAD
const addTokenToPayload = (client) => {
  client.interceptors.request.use(
    (config) => {
      const token = getToken();

      if (token) {
        // Add token to payload instead of headers
        if (config.method?.toLowerCase() !== "get") {
          config.data = {
            ...config.data,
            token: token,
          };
        } else {
          // For GET requests, add to params
          config.params = {
            ...config.params,
            token: token,
          };
        }
        console.log(
          `✅ [PAYLOAD] Auth token added to payload for ${config.method.toUpperCase()} ${config.url}`,
        );
      } else {
        console.warn(
          `⚠️ [PAYLOAD] No auth token available for ${config.method.toUpperCase()} ${config.url}`,
        );
      }

      // Reset auto-logout timer on API calls
      if (globalResetTimer && typeof globalResetTimer === "function") {
        globalResetTimer();
      }

      return config;
    },
    (error) => {
      throw error;
    },
  );
};

// Response interceptor - Handle errors
const addResponseInterceptor = (client) => {
  client.interceptors.response.use(
    (response) => {
      console.log(
        `✅ [PAYLOAD] Success: ${response.config.method.toUpperCase()} ${response.config.url}`,
      );
      return response;
    },
    async (error) => {
      const status = error.response?.status;
      const originalRequest = error.config;

      globalErrorHandler.handleError(error, "API Response (Payload)");

      if (status === 401) {
        handleUnauthorized();
        throw error;
      }

      // Handle 403 Forbidden - retry with fresh CSRF token
      if (status === 403) {
        const shouldClearCSRF = globalErrorHandler.handleForbidden(error);

        if (shouldClearCSRF && !originalRequest._csrfRetry) {
          console.warn(
            "⚠️ [PAYLOAD] 403 Forbidden - Retrying with fresh CSRF token...",
          );

          originalRequest._csrfRetry = true;
          csrfManager.clearToken();

          try {
            const token = getToken();

            if (!token) {
              console.error("❌ [PAYLOAD] No auth token for CSRF retry");
              throw error;
            }

            const newCsrfToken = await csrfManager.refreshToken({
              Authorization: `${token}`,
            });

            // Update payload with new CSRF token
            if (originalRequest.method?.toLowerCase() !== "get") {
              originalRequest.data = {
                ...originalRequest.data,
                csrfToken: newCsrfToken,
              };
            } else {
              originalRequest.params = {
                ...originalRequest.params,
                csrfToken: newCsrfToken,
              };
            }

            console.log("🔄 [PAYLOAD] Retrying with fresh CSRF token");
            return client(originalRequest);
          } catch (refreshError) {
            console.error("❌ [PAYLOAD] Failed to refresh CSRF token");
            throw refreshError;
          }
        }
      }

      throw error;
    },
  );
};

// Apply interceptors
addTokenToPayload(axiosClientPayload);
addResponseInterceptor(axiosClientPayload);

export default axiosClientPayload;
